import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Badge } from '@/components/ui/Badge'
import { Avatar } from '@/components/ui/Avatar'
import {
  useTicket,
  useUpdateTicket,
  useTicketMessages,
  useTicketMessagesRealtime,
  useTicketUpdatesRealtime,
  useSendMessage,
  useAssignTicketToMe,
  useUnassignTicket,
} from '@/hooks/useTickets'
import { useAuth } from '@/contexts/AuthContext'
import {
  ArrowLeft,
  Send,
  User,
  Mail,
  Calendar,
  MessageSquare,
  AlertCircle,
  UserPlus,
  UserMinus,
} from 'lucide-react'
import type { TicketStatus, TicketPriority } from '@/types/database'
import { formatDistanceToNow, format } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'

const statusConfig: Record<TicketStatus, { label: string; color: 'mint' | 'gold' | 'brown' }> = {
  open: { label: 'Ouvert', color: 'mint' },
  in_progress: { label: 'En cours', color: 'gold' },
  closed: { label: 'Fermé', color: 'brown' },
}

const priorityConfig: Record<TicketPriority, { label: string; color: 'mint' | 'gold' | 'brown' }> = {
  low: { label: 'Basse', color: 'mint' },
  medium: { label: 'Moyenne', color: 'gold' },
  high: { label: 'Haute', color: 'brown' },
}

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [message, setMessage] = useState('')

  const { data: ticket, isLoading } = useTicket(id)
  const { data: messages = [], isLoading: messagesLoading } = useTicketMessages(id)
  const updateTicket = useUpdateTicket()
  const sendMessage = useSendMessage()
  const assignToMe = useAssignTicketToMe()
  const unassign = useUnassignTicket()

  // Real-time subscriptions
  useTicketMessagesRealtime(id)
  useTicketUpdatesRealtime(id)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!id) return
    try {
      await updateTicket.mutateAsync({
        id,
        updates: { status: newStatus },
      })
      toast.success('Statut mis à jour')
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut')
    }
  }

  const handlePriorityChange = async (newPriority: TicketPriority) => {
    if (!id) return
    try {
      await updateTicket.mutateAsync({
        id,
        updates: { priority: newPriority },
      })
      toast.success('Priorité mise à jour')
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la priorité')
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim() || !id || !user) return

    try {
      await sendMessage.mutateAsync({
        content: message.trim(),
        ticket_id: id,
        sender_id: user.id,
        sender_type: 'support',
      })
      setMessage('')
      toast.success('Message envoyé')
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message')
    }
  }

  const handleAssignToMe = async () => {
    if (!id) return
    try {
      await assignToMe.mutateAsync(id)
      toast.success('Ticket assigné à vous')
    } catch (error) {
      toast.error('Erreur lors de l\'assignation')
    }
  }

  const handleUnassign = async () => {
    if (!id) return
    try {
      await unassign.mutateAsync(id)
      toast.success('Assignation retirée')
    } catch (error) {
      toast.error('Erreur lors du retrait de l\'assignation')
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-brown-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-brown-900 mb-2">Ticket introuvable</h2>
        <p className="text-brown-600 mb-4">Ce ticket n'existe pas ou a été supprimé.</p>
        <Link to="/tickets">
          <Button>Retour aux tickets</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/tickets')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold text-brown-900">
              {ticket.title}
            </h1>
            <p className="text-brown-600">
              Créé {formatDistanceToNow(new Date(ticket.created_at), {
                addSuffix: true,
                locale: fr,
              })}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content - Chat */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket description */}
          <Card>
            <h2 className="text-lg font-semibold text-brown-900 mb-3">Description</h2>
            <p className="text-brown-700 whitespace-pre-wrap">{ticket.description}</p>
          </Card>

          {/* Messages */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-brown-600" />
              <h2 className="text-lg font-semibold text-brown-900">
                Conversation ({messages.length})
              </h2>
            </div>

            {messagesLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Messages list */}
                <div className="max-h-[500px] overflow-y-auto space-y-4 pr-2">
                  {messages.map((msg) => {
                    const isAdmin = msg.sender_type === 'support'
                    return (
                      <div
                        key={msg.id}
                        className={`flex gap-3 ${isAdmin ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        <Avatar
                          name={isAdmin ? 'Admin' : ticket.user?.full_name || 'Client'}
                          size="sm"
                        />
                        <div className={`flex-1 max-w-[80%] ${isAdmin ? 'items-end' : 'items-start'}`}>
                          <div
                            className={`rounded-2xl px-4 py-3 ${
                              isAdmin
                                ? 'bg-mint-600 text-white rounded-br-none'
                                : 'bg-brown-100 text-brown-900 rounded-bl-none'
                            }`}
                          >
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          </div>
                          <p className={`text-xs text-brown-500 mt-1 px-2 ${isAdmin ? 'text-right' : 'text-left'}`}>
                            {format(new Date(msg.created_at), 'PPp', { locale: fr })}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Send message form */}
                {ticket.status !== 'closed' && (
                  <form onSubmit={handleSendMessage} className="border-t border-brown-200 pt-4">
                    <div className="flex gap-3">
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Écrire une réponse..."
                        rows={3}
                        className="flex-1"
                        disabled={sendMessage.isPending}
                      />
                      <Button
                        type="submit"
                        disabled={!message.trim() || sendMessage.isPending}
                        leftIcon={<Send className="w-4 h-4" />}
                      >
                        Envoyer
                      </Button>
                    </div>
                  </form>
                )}

                {ticket.status === 'closed' && (
                  <div className="border-t border-brown-200 pt-4">
                    <p className="text-center text-brown-600 text-sm">
                      Ce ticket est fermé. Modifiez le statut pour répondre.
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar - Ticket info */}
        <div className="space-y-6">
          {/* Status & Priority */}
          <Card>
            <h3 className="text-sm font-medium text-brown-900 mb-3">Statut & Priorité</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-brown-600 mb-1">Statut</label>
                <Select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
                  disabled={updateTicket.isPending}
                >
                  <option value="open">Ouvert</option>
                  <option value="in_progress">En cours</option>
                  <option value="closed">Fermé</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-brown-600 mb-1">Priorité</label>
                <Select
                  value={ticket.priority}
                  onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)}
                  disabled={updateTicket.isPending}
                >
                  <option value="low">Basse</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                </Select>
              </div>
            </div>
          </Card>

          {/* Assignment */}
          <Card>
            <h3 className="text-sm font-medium text-brown-900 mb-3">Assignation</h3>
            {ticket.assigned_to ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Avatar
                    name={ticket.assigned_admin?.full_name || 'Admin'}
                    src={ticket.assigned_admin?.avatar_url || undefined}
                    size="sm"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-brown-900">
                      {ticket.assigned_admin?.full_name || 'Admin'}
                    </p>
                    <p className="text-xs text-brown-600">
                      {ticket.assigned_admin?.email}
                    </p>
                  </div>
                </div>
                {ticket.assigned_to === user?.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUnassign}
                    disabled={unassign.isPending}
                    leftIcon={<UserMinus className="w-4 h-4" />}
                    className="w-full"
                  >
                    Me désassigner
                  </Button>
                )}
              </div>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAssignToMe}
                disabled={assignToMe.isPending}
                leftIcon={<UserPlus className="w-4 h-4" />}
                className="w-full"
              >
                M'assigner ce ticket
              </Button>
            )}
          </Card>

          {/* Client info */}
          <Card>
            <h3 className="text-sm font-medium text-brown-900 mb-3">Informations client</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-brown-500" />
                <span className="text-brown-700">
                  {ticket.user?.full_name || 'Client'}
                </span>
              </div>
              {ticket.user?.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-brown-500" />
                  <a
                    href={`mailto:${ticket.user.email}`}
                    className="text-mint-600 hover:text-mint-700 text-sm"
                  >
                    {ticket.user.email}
                  </a>
                </div>
              )}
            </div>
          </Card>

          {/* Project info */}
          {ticket.project && (
            <Card>
              <h3 className="text-sm font-medium text-brown-900 mb-3">Projet associé</h3>
              <Link
                to={`/projects/${ticket.project_id}`}
                className="text-mint-600 hover:text-mint-700 font-medium"
              >
                {ticket.project.title}
              </Link>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <h3 className="text-sm font-medium text-brown-900 mb-3">Métadonnées</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-brown-600">
                <Calendar className="w-4 h-4" />
                <span>
                  Créé le {format(new Date(ticket.created_at), 'PPp', { locale: fr })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-brown-600">
                <Calendar className="w-4 h-4" />
                <span>
                  Modifié le {format(new Date(ticket.updated_at), 'PPp', { locale: fr })}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
